import type { CustomerReferenceTag, CampaignProduct } from '~/types'

export const useProductStateManagement = (products: CampaignProduct) => {
  const cartStore = useCart()
  const user = useUserStore()
  const supplier = useSupplier()
  const { currencyToString } = useHelper()
  const selectedProduct = ref(products.items.find(item => item.product?.showAsFirstChoice) ?? products.items[0])
  const selectedProductQuantities = ref<Record<string, Record<CustomerReferenceTag, number>>>({})

  const initialReference: CustomerReferenceTag
    = selectedProduct.value.references?.length === 1
      ? selectedProduct.value.references[0]
      : selectedProduct.value.preselectedReference ?? 'office'
  const reference = ref<CustomerReferenceTag>(initialReference)
  const quantity = ref(currentQuantity(reference.value) ?? 0)
  const clinicChain = computed(() => {
    // TODO: Remove this once we replace the old search product data with the new one
    if ((selectedProduct.value.clinicChains?.length ?? 0) > 0) {
      return selectedProduct.value.clinicChains?.find(cc => cc.handle === user.data?.clinicChain?.handle)
    }
    else if (Object.keys(user.data?.clinicChain ?? {}).length > 0) {
      return selectedProduct.value.clinicChain
    }
    return undefined
  })
  const selectedProductSupplier = computed(() => supplier.suppliers.find(s => s.handle === products.handle))

  const isFavourite = ref<boolean>(!!selectedProduct.value.isFavorite)
  const isSubscribed = ref<boolean>(!!selectedProduct.value.isSubscribed)

  const configurations = ref(
    products.items
      .flatMap(item => Object.entries(item.fields))
      .reduce(groupByKey, {} as Record<string, string[]>),
  )

  function currentQuantity(refTag: CustomerReferenceTag) {
    const cartQuantity = cartStore.carts
      .flatMap(cart => cart.items)
      .find(item =>
        item.handle === selectedProduct.value.handle
        && item.reference === refTag.toUpperCase(),
      )
      ?.quantity ?? 0
    const initialQuantity = selectedProductQuantities.value[selectedProduct.value.handle]?.[refTag]
    return initialQuantity ?? cartQuantity
  }

  // Normalize scale price to a plain number (EUR cents).
  // Legacy/campaign scales carry `price: number`; hybrid scales carry `price: { amount, currency }`.
  const priceOf = (scale: { price: number | { amount: number } }): number =>
    typeof scale.price === 'number' ? scale.price : scale.price.amount

  const currentPrice = computed(() => {
    const listPrice = selectedProduct.value.listPrice
    const scalePrices = selectedProduct.value.scalePrices
    if (!scalePrices || scalePrices.length === 0) {
      return listPrice
    }
    else if (scalePrices.some(scalePrice => scalePrice.minQuantity === 1) && quantity.value < 1) {
      return priceOf(scalePrices.filter(scale => scale.minQuantity === 1)[0])
    }

    const applicablePrice = scalePrices
      // Legacy scales carry productHandle; hybrid scales don't — keep both.
      .filter(scale => !scale.productHandle || scale.productHandle === selectedProduct.value.handle)
      .sort((a, b) => b.minQuantity - a.minQuantity)
      .find(scale => quantity.value >= scale.minQuantity)

    return applicablePrice ? priceOf(applicablePrice) : listPrice
  })

  function changeSelected(key: string, value: string) {
    const configuration: Record<string, string> = { ...selectedProduct.value.fields, [key]: value }
    selectedProductQuantities.value[selectedProduct.value.handle] = {
      ...selectedProductQuantities.value[selectedProduct.value.handle],
      [reference.value]: quantity.value,
    }
    selectedProduct.value
      = products.items.find(item => Object.entries(item.fields).every(([k, v]) => configuration[k] === v))
        ?? products.items.find(item => item.fields[key] === value)!
    quantity.value = currentQuantity(reference.value)
    if (reference.value === selectedProduct.value.preselectedTag) {
      reference.value = selectedProduct.value.preselectedTag
    }
    isFavourite.value = !!selectedProduct.value.isFavorite
    isSubscribed.value = !!selectedProduct.value.isSubscribed
  }

  const productDetailUrl = computed(() =>
    `/product/${products.handle}/${encodeURIComponent(selectedProduct.value.idExternal)}`,
  )

  function updateSelectedReferenceName(value?: CustomerReferenceTag) {
    if (!value) return

    selectedProductQuantities.value[selectedProduct.value.handle] = {
      ...selectedProductQuantities.value[selectedProduct.value.handle],
      [reference.value]: quantity.value,
    }

    quantity.value = currentQuantity(value)
    reference.value = value
  }

  return {
    selectedProduct,
    selectedProductSupplier,
    isFavourite,
    isSubscribed,
    quantity,
    reference,
    clinicChain,
    productDetailUrl,
    currentPrice: computed(() => currencyToString(currentPrice.value)),
    currentPriceCents: currentPrice,
    configurations,
    changeSelected,
    updateSelectedReferenceName,
  }
}
